// InteractionTasksPanel — Super-Admin editor for the visitor-billing "dots"
// system (site-wide). Lives at the bottom of Platform Config. Lets the admin:
//   • rename task labels, change dots per task (0–10), enable/disable tasks
//   • add custom tasks earned by a server-verifiable trigger
//   • set the $1 "gift ceiling" for free ($0) subscribers
// Jun 8, 2026. Later this may cascade to creator-level + per-silo overrides.
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CaretDown, CaretRight, SquaresFour, Plus, Trash, FloppyDisk, ToggleLeft, ToggleRight,
} from "@phosphor-icons/react";

const newCustomKey = () => `custom_${Math.random().toString(36).slice(2, 8)}`;

export default function InteractionTasksPanel() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [giftDollars, setGiftDollars] = useState("1");
  const [triggers, setTriggers] = useState([]);
  const [dotMax, setDotMax] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/interaction-config");
      setTasks(r.data.tasks || []);
      setGiftDollars(((r.data.free_sub_gift_cents || 0) / 100).toString());
      setTriggers(r.data.triggers || []);
      setDotMax(r.data.dot_max || 10);
    } catch {
      toast.error("Couldn't load the interaction config.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const patchTask = (i, patch) =>
    setTasks((ts) => ts.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const removeTask = (i) => setTasks((ts) => ts.filter((_, idx) => idx !== i));

  const addCustom = () =>
    setTasks((ts) => [
      ...ts,
      { key: newCustomKey(), label: "New custom task", dots: 1, enabled: true,
        builtin: false, trigger: triggers[0]?.id || null, threshold: triggers[0]?.needs_threshold ? 10 : null },
    ]);

  const save = async () => {
    // guard: custom tasks need a trigger
    for (const t of tasks) {
      if (!t.builtin && !t.trigger) { toast.error(`"${t.label}" needs a trigger.`); return; }
      if (!t.label?.trim()) { toast.error("Every task needs a label."); return; }
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/interaction-config", {
        free_sub_gift_cents: Math.round(parseFloat(giftDollars || "0") * 100),
        tasks: tasks.map((t) => ({
          key: t.key, label: t.label, dots: Number(t.dots) || 0, enabled: !!t.enabled,
          trigger: t.builtin ? null : t.trigger,
          threshold: t.builtin ? null : (t.threshold === "" ? null : t.threshold),
        })),
      });
      toast.success("Interaction system saved — live site-wide.");
      // re-sync from server (clamped values etc.)
      const cfg = r.data.interaction_config || {};
      if (cfg.tasks) setTasks(cfg.tasks.map((t) => ({ ...t, builtin: !!t.builtin })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const triggerNeedsThreshold = (id) => triggers.find((x) => x.id === id)?.needs_threshold;

  return (
    <section className="mt-12" data-testid="admin-interaction-tasks-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left py-2 border-b hairline hover:border-[#4A5D4E] transition-colors"
        data-testid="interaction-tasks-toggle"
        aria-expanded={open}
      >
        <h2 className="font-serif-display text-2xl flex items-center gap-2">
          <SquaresFour size={20} weight="duotone" /> Interaction system (the "dots")
        </h2>
        {open ? <CaretDown size={20} weight="bold" className="text-[#6B6A68]" />
              : <CaretRight size={20} weight="bold" className="text-[#6B6A68]" />}
      </button>

      {open && (
        <div className="mt-4" data-testid="interaction-tasks-body">
          <p className="text-sm text-[#6B6A68] mb-4 max-w-3xl">
            Site-wide control of the visitor question allowance. Rename steps, change how many
            questions ("dots", 0–{dotMax}) each grants, turn steps off, or add custom steps earned by a
            real in-app action. These apply to every silo for now.
          </p>

          {loading ? (
            <p className="text-sm text-[#6B6A68]">Loading…</p>
          ) : (
            <>
              {/* Free-subscriber gift ceiling */}
              <div className="mb-5 rounded-md border hairline bg-[#F7F4EE] px-4 py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1C1B1A]">Free-silo gift ceiling</p>
                  <p className="text-[12px] text-[#6B6A68] max-w-md">
                    When a silo is $0, a subscriber gets unlimited questions up to this much AI cost
                    each month (the owner's gift), then they're nudged to tip.
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <span className="text-sm text-[#6B6A68]">$</span>
                  <input
                    type="number" min="0" step="0.50" value={giftDollars}
                    onChange={(e) => setGiftDollars(e.target.value)}
                    data-testid="gift-ceiling-input"
                    className="w-24 text-sm rounded-md border border-[#E5E5E0] bg-white px-2 py-1.5 focus:outline-none focus:border-[#4A5D4E]"
                  />
                  <span className="text-sm text-[#6B6A68]">/mo</span>
                </div>
              </div>

              {/* Task rows */}
              <div className="space-y-2">
                {tasks.map((t, i) => (
                  <div key={t.key} data-testid={`task-row-${t.key}`}
                    className={`rounded-md border hairline bg-white px-3 py-2.5 ${t.enabled ? "" : "opacity-60"}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => patchTask(i, { enabled: !t.enabled })}
                        data-testid={`task-toggle-${t.key}`} title={t.enabled ? "Enabled" : "Disabled"}
                        className="shrink-0 text-[#4A5D4E]">
                        {t.enabled ? <ToggleRight size={26} weight="fill" />
                                   : <ToggleLeft size={26} weight="fill" className="text-[#C9C7C2]" />}
                      </button>
                      <input
                        value={t.label}
                        onChange={(e) => patchTask(i, { label: e.target.value })}
                        data-testid={`task-label-${t.key}`}
                        className="flex-1 min-w-[160px] text-sm rounded-md border border-[#E5E5E0] bg-white px-2.5 py-1.5 focus:outline-none focus:border-[#4A5D4E]"
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <input
                          type="number" min="0" max={dotMax} value={t.dots}
                          onChange={(e) => patchTask(i, { dots: e.target.value })}
                          data-testid={`task-dots-${t.key}`}
                          className="w-16 text-sm rounded-md border border-[#E5E5E0] bg-white px-2 py-1.5 text-center focus:outline-none focus:border-[#4A5D4E]"
                        />
                        <span className="text-[11px] text-[#6B6A68]">dots</span>
                      </div>
                      {t.builtin ? (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-[#9A9892] px-2 shrink-0">built-in</span>
                      ) : (
                        <button onClick={() => removeTask(i)} data-testid={`task-delete-${t.key}`}
                          className="shrink-0 text-[#C16E5A] hover:text-[#a85946] p-1" title="Delete custom task">
                          <Trash size={16} weight="bold" />
                        </button>
                      )}
                    </div>

                    {/* Custom-task trigger config */}
                    {!t.builtin && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap pl-9">
                        <span className="text-[11px] text-[#6B6A68]">Earned when:</span>
                        <select
                          value={t.trigger || ""}
                          onChange={(e) => patchTask(i, { trigger: e.target.value,
                            threshold: triggerNeedsThreshold(e.target.value) ? (t.threshold ?? 10) : null })}
                          data-testid={`task-trigger-${t.key}`}
                          className="text-[12px] rounded-md border border-[#E5E5E0] bg-white px-2 py-1.5 focus:outline-none focus:border-[#4A5D4E]"
                        >
                          {triggers.map((tr) => <option key={tr.id} value={tr.id}>{tr.label}</option>)}
                        </select>
                        {triggerNeedsThreshold(t.trigger) && (
                          <input
                            type="number" min="1" value={t.threshold ?? ""}
                            onChange={(e) => patchTask(i, { threshold: e.target.value })}
                            data-testid={`task-threshold-${t.key}`}
                            placeholder="N"
                            className="w-20 text-[12px] rounded-md border border-[#E5E5E0] bg-white px-2 py-1.5 focus:outline-none focus:border-[#4A5D4E]"
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button onClick={addCustom} data-testid="add-custom-task-btn"
                  className="text-sm px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] text-[#1C1B1A] flex items-center gap-1.5">
                  <Plus size={15} weight="bold" /> Add custom task
                </button>
                <button onClick={save} disabled={saving} data-testid="save-interaction-config-btn"
                  className="text-sm px-4 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1.5">
                  <FloppyDisk size={15} weight="fill" /> {saving ? "Saving…" : "Save (site-wide)"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
